/** BMS payloads may arrive as objects or JSON strings from the API */
export function normalizeBmsRawInput(raw: unknown): Record<string, unknown> | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === "string") {
    try {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

export function num(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** SoC % = capacity_left / capacity_full * 100 (поле `soc` з BMS не використовуємо). */
export function deriveBmsSocPercentFromCapacities(
  data: Record<string, unknown>
): number | undefined {
  const left = num(data.capacity_left);
  const full = num(data.capacity_full);
  if (left === undefined || full === undefined || full <= 0) {
    return undefined;
  }
  const pct = (left / full) * 100;
  if (!Number.isFinite(pct)) {
    return undefined;
  }
  return Math.round(Math.max(0, Math.min(100, pct)));
}

/** Typical “no sensor” sentinels around absolute zero */
function isPlausibleBmsTemperature(celsius: number): boolean {
  return celsius > -80 && celsius < 120;
}

export interface BmsSummary {
  /** Відсоток з ємностей (capacity_left / capacity_full), не з поля BMS `soc`. */
  soc?: number;
  packVoltage?: number;
  current?: number;
  cycles?: number;
  capacityFull?: number;
  capacityLeft?: number;
  deltaVoltage?: number;
  maxCellVoltage?: number;
  minCellVoltage?: number;
  firmware?: number | string;
  bmsDate?: string;
  macAddress?: string;
  validTemperatures: number[];
  cells: { key: string; voltage: number }[];
}

export function buildBmsSummary(data: Record<string, unknown>): BmsSummary {
  const tempsObj =
    typeof data.temperatures === "object" && data.temperatures !== null
      ? (data.temperatures as Record<string, unknown>)
      : {};
  const validTemperatures = Object.values(tempsObj)
    .map((v) => num(v))
    .filter((v): v is number => v !== undefined && isPlausibleBmsTemperature(v));

  const cellsObj =
    typeof data.cells === "object" && data.cells !== null
      ? (data.cells as Record<string, unknown>)
      : {};
  const cells = Object.entries(cellsObj)
    .map(([key, value]) => ({ key, voltage: num(value) }))
    .filter((item): item is { key: string; voltage: number } => item.voltage !== undefined)
    .sort((a, b) => {
      const na = parseInt(a.key.replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(b.key.replace(/\D/g, ""), 10) || 0;
      return na - nb;
    });

  return {
    soc: deriveBmsSocPercentFromCapacities(data),
    packVoltage: num(data.voltage),
    current: num(data.current),
    cycles: num(data.cycles),
    capacityFull: num(data.capacity_full),
    capacityLeft: num(data.capacity_left),
    deltaVoltage: num(data.delta_voltage),
    maxCellVoltage: num(data.max_cell_voltage),
    minCellVoltage: num(data.min_cell_voltage),
    firmware: (() => {
      const n = num(data.firmware);
      if (n !== undefined) {
        return n;
      }
      if (typeof data.firmware === "string" && data.firmware.trim()) {
        return data.firmware;
      }
      return undefined;
    })(),
    bmsDate: typeof data.date === "string" ? data.date : undefined,
    macAddress: typeof data.macAddress === "string" ? data.macAddress : undefined,
    validTemperatures,
    cells,
  };
}

export function hasAnyBmsMetric(summary: BmsSummary): boolean {
  return (
    summary.soc !== undefined ||
    summary.packVoltage !== undefined ||
    summary.current !== undefined ||
    summary.cycles !== undefined ||
    summary.capacityFull !== undefined ||
    summary.capacityLeft !== undefined ||
    summary.deltaVoltage !== undefined ||
    summary.maxCellVoltage !== undefined ||
    summary.minCellVoltage !== undefined ||
    summary.firmware !== undefined ||
    summary.bmsDate !== undefined ||
    summary.macAddress !== undefined ||
    summary.validTemperatures.length > 0 ||
    summary.cells.length > 0
  );
}
